import type { AppState, DashboardState, GlobalUiState } from '@protector/entities/__legacy__/redux.types';
import { Entitlements } from '@protector/entities/licensing/constants';
import type { PropertyState } from '@protector/entities/property/__legacy__/redux.types';
import type { PropertyInfoAddress, SeasonAreasInDate } from '@protector/entities/property/models';
import type { RegionState } from '@protector/entities/region/__legacy__/redux.types';
import type { Nullable } from '@protector/utils/types';
import type { CompanyState } from '@protector/entities/company/__legacy__/redux.types';
import type { LicensingStatusAppsResponse, LicensingStatusResponse } from '@protector/entities/company/models';
import type { JDOCState } from '@protector/entities/integration/__legacy__/redux.types';
import { isActive } from 'entities/season/season.functions';
import type { PermissionFlagEntitlement, PermissionFlags } from './services/auth/auth.models';

export {
  getCurrentSeasonFields,
  getPropertyEntitiesInEntities,
  getSelectedPropertyId,
  getSelectedSeasons
} from '@protector/entities/core/__legacy__/redux.selectors';

export type Selector<T> = (state: AppState) => T;

export const getSeverityMapByIssue: Selector<boolean> = state => {
  let hasActiveSeasonSelected = false;
  const allSeasons = state.entities.season.entities || {};
  const selectedSeasons = state.uiState.global.selectedSeasons || [];
  hasActiveSeasonSelected = selectedSeasons.some(selectedSeason => isActive(allSeasons[selectedSeason]));
  return (
    !!state.entities.company.licensingStatus?.apps.find(app =>
      app.plan.entitlements.find(entitlement => entitlement.key === Entitlements.MAPS_SEVERITY_TYPE_PHENOMENON)
    ) && hasActiveSeasonSelected
  );
};

export const getSystemFlags: Selector<GlobalUiState['systemFlags']> = state => state.uiState.global.systemFlags;
export const getPermissionFlags: Selector<PermissionFlags> = (state: AppState) => state.uiState.auth.permissionFlags;

export const getPermissionFlagTasksNotificationEmail: Selector<boolean> = state =>
  state.uiState.auth.permissionFlags?.tasksNotificationEmail?.value || false;

export const getPermissionFlagNemaDigital: Selector<PermissionFlagEntitlement['value']> = state =>
  state.uiState.auth.permissionFlags?.nemaDigital?.value || false;

export const getPermissionFlagErpOrg: Selector<PermissionFlagEntitlement['value']> = state =>
  state.uiState.auth.permissionFlags?.erpOrg?.value || false;

export const getRegionEntitiesInEntities: Selector<RegionState['entities']> = state => state.entities.region.entities;

export const getCompanyEntitiesInEntities: Selector<CompanyState['entities']> = state => state.entities.company.entities;

export const getRegionsErrorsInEntities: Selector<RegionState['error']> = state => state.entities.region.error;

export const getCurrentAreaId: Selector<GlobalUiState['selectedArea']> = state => state.uiState.global.selectedArea;

export const getIsCurrentInfoLoaded: Selector<RegionState['isCurrentInfoLoaded']> = state => state.entities.region.isCurrentInfoLoaded;

export const getIsCurrentInfoLoading: Selector<RegionState['isCurrentInfoLoading']> = state => state.entities.region.isCurrentInfoLoading;

export const getIsMethodologyLoaded: Selector<RegionState['isMethodologyLoaded']> = state => state.entities.region.isMethodologyLoaded;

export const getEntitiesRegionIsLoading: Selector<RegionState['isLoading']> = state => state.entities.region.isLoading;

export const getIsLoadingCurrentAreaSeasons: Selector<PropertyState['isLoadingCurrentAreaSeasons']> = state =>
  state.entities.property.isLoadingCurrentAreaSeasons;

export const getIsLoadingGeometry: Selector<RegionState['isLoadingGeometry']> = state => state.entities.region.isLoadingGeometry;

export const getGettingUpdateDiff: Selector<JDOCState['gettingUpdateDiff']> = state => state.entities.integrations.jdoc.gettingUpdateDiff;

export const getUpdateDiff: Selector<JDOCState['updateDiff']> = state => state.entities.integrations.jdoc.updateDiff;

export const getSelectedDateRange: Selector<DashboardState['selectedDateRange']> = state =>
  state.uiState.global.dashboardState.selectedDateRange;

export const getChoroplethLimits: Selector<RegionState['choroplethLimits']> = state => state.entities.region.choroplethLimits;

export const getCurrentUnitSystem: Selector<GlobalUiState['currentUnitSystem']> = state => state.uiState.global.currentUnitSystem;

export const getSeasonAreasInDateContent: Selector<SeasonAreasInDate['content']> = state =>
  state.entities.property.seasonAreasInDate?.content;

export const getLicensingStatusApps: Selector<LicensingStatusResponse['apps'] | undefined> = state =>
  state.entities.company.licensingStatus?.apps;

export const getCurrentWorkspace: Selector<LicensingStatusResponse['workspace'] | undefined> = state =>
  state.entities.company.licensingStatus?.workspace;

export const getLicensingStatus: Selector<CompanyState['licensingStatus']> = (state: AppState) => state.entities.company.licensingStatus;

export const getLicenseApps: Selector<LicensingStatusAppsResponse[]> = state => state.entities.company.licensingStatus?.apps ?? [];

export const getPropertyData: Selector<Nullable<PropertyInfoAddress>> = (state: AppState) => state.entities.property.propertyData;

export const getPropertyTimezone: Selector<PropertyInfoAddress['timeZone'] | undefined> = state =>
  state.entities.property.propertyData?.timeZone;

export const getInterfaceLanguage: Selector<GlobalUiState['interfaceLanguage']> = state => state.uiState.global.interfaceLanguage;

export const getSelectedCompanyId = (state: AppState) => state.uiState.global.selectedCompany;

export const selectSelectedCompany = (state: GlobalUiState) => state.selectedCompany;
